import React, { useEffect, useState } from "react";
import { InputSwitch } from "primereact/inputswitch";
import DropdownField from "../../../shared/DropdownField";
import MultiSelectField from "../../../shared/MultiSelectField";
import { invoiceSizeOptions } from "../../../constants/SettingConstnants";
import { useTranslation } from "react-i18next";
import ReusableModal from "../../../shared/ReuseableModal";
import {
  FaQrcode,
  FaFileInvoice,
  FaWhatsapp,
  FaLink,
  FaCog,
  FaSave,
  FaEdit,
  FaShieldAlt,
  FaCalendarCheck,
} from "react-icons/fa";
import WhatsappQr from "./WhatsappQr";
import { FaPrescriptionBottle } from "react-icons/fa6";
import TextInput from "../../../shared/TextInput";
import { fetchData } from "../../../helpers/fetchData";
import toast from "react-hot-toast";
import CustomButton from "../../../shared/CustomButton";
import MedicalReportTemplateSettings from "./MedicalReportTemplateSettings";

const providers = [
  { label: "Nahdi", value: "nahdi", img: "/images/provider1.jpeg" },
];

const SettingsSection = ({ header, children, className = "" }) => (
  <section
    className={`overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm ${className}`}
  >
    <div className="border-b border-gray-200 bg-gray-50 px-5 py-4 [&>div>div:first-child]:hidden">
      {header}
    </div>
    {children}
  </section>
);

function GeneralSettings({
  settings,
  setSettings,
  loading,
  onSettingsChange,
  updateSettings,
  updateLoading,
  activeSectionIndex = null,
}) {
  const { t } = useTranslation();
  const showSection = (index) =>
    activeSectionIndex === null || activeSectionIndex === index;
  const [showQr, setShowQr] = useState(false);
  const [whatsappValue, setWhatsappValue] = useState(null);
  const [showPharmacyModal, setShowPharmacyModal] = useState(false);
  const [pharmacyLoading, setPharmacyLoading] = useState(false);
  const [pharmacyEnabled, setPharmacyEnabled] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [nahdiForm, setNahdiForm] = useState({
    provider: "",
    nahdi_provider_id: "",
    nahdi_provider_token: "",
    show_default_pharmacy: false,
  });

  const loadPharmacyState = () => {
    const nahdiSettings = settings?.invoice_size?.nahdi_settings || {};
    setPharmacyEnabled(!!nahdiSettings?.has_nahdi_pharmacy);
    setNahdiForm((prev) => ({
      ...prev,
      provider: nahdiSettings?.provider_name || providers?.[0]?.value || "",
      nahdi_provider_id: nahdiSettings?.nahdi_provider_id || "",
      nahdi_provider_token: nahdiSettings?.nahdi_provider_token || "",
      show_default_pharmacy: nahdiSettings?.show_default_pharmacy ?? false,
    }));
  };

  useEffect(() => {
    loadPharmacyState();
  }, [settings]);

  useEffect(() => {
    if (activeSectionIndex !== 7) return;
    fetchData("POST", "/v3/department/getList", {
      page: 1,
      per_page: 1000,
    }).then((response) => setDepartments(response?.data || []));
  }, [activeSectionIndex]);

  const discountSetting = settings.discount_settings;
  const discountConfig = discountSetting?.discount_settings || {
    default_limit: 100,
    department_limits: [],
  };
  const defaultDiscountLimit =
    Number(discountConfig.default_limit) > 0
      ? Number(discountConfig.default_limit)
      : 100;
  const getDepartmentLimit = (id) => {
    const configuredLimit = discountConfig.department_limits?.find(
      (item) => String(item.department_id) === String(id),
    )?.limit;
    return configuredLimit !== "" && Number(configuredLimit) > 0
      ? Number(configuredLimit)
      : defaultDiscountLimit;
  };
  const updateDepartmentLimit = (id, value) =>
    setSettings((prev) => ({
      ...prev,
      discount_settings: {
        ...discountSetting,
        discount_settings: {
          ...discountConfig,
          department_limits: [
            ...(discountConfig.department_limits || []).filter(
              (item) => String(item.department_id) !== String(id),
            ),
            { department_id: String(id), limit: value },
          ].sort((a, b) =>
            String(a.department_id).localeCompare(String(b.department_id)),
          ),
        },
      },
    }));

  const updateWhatsapp = () => {
    if (!whatsappValue) return;
    const selectedValue =
      whatsappValue?.target?.value === "true" ||
      whatsappValue?.target?.value === true;
    updateSettings({
      ...settings?.invoice_size,
      whatsapp: {
        status: selectedValue,
        module_name: settings?.invoice_size?.whatsapp?.module_name || null,
      },
    });
    setShowQr(false);
  };

  const refreshSettingsList = async () => {
    const res = await fetchData("POST", "/v3/settings/list");
    if (res?.data?.length) {
      const updatedSettings = {};
      res.data.forEach((setting) => {
        updatedSettings[setting.name] = setting;
      });
      setSettings(updatedSettings);
    }
  };

  const disablePharmacySettings = async (enabled) => {
    if (enabled) return;
    setPharmacyLoading(true);
    try {
      const res = await fetchData("POST", "/v3/settings/nahdi", {
        has_nahdi_pharmacy: false,
        provider_name: "",
        nahdi_provider_id: "",
        nahdi_provider_token: "",
        show_default_pharmacy: false,
      });
      if (res) {
        toast.success(res?.message || t("Pharmacy disabled"));
        setPharmacyEnabled(false);
        setShowPharmacyModal(false);
        setNahdiForm((prev) => ({
          ...prev,
          provider: providers?.[0]?.value || "",
          nahdi_provider_id: "",
          nahdi_provider_token: "",
          show_default_pharmacy: false,
        }));
        await refreshSettingsList();
      }
    } finally {
      setPharmacyLoading(false);
    }
  };

  const saveNahdiSettings = async () => {
    if (!nahdiForm.provider) {
      toast.error(t("Please select a provider"));
      return;
    }
    if (!nahdiForm.nahdi_provider_id || !nahdiForm.nahdi_provider_token) {
      toast.error(t("Provider ID and Provider Token are required"));
      return;
    }
    setPharmacyLoading(true);
    try {
      const res = await fetchData("POST", "/v3/settings/nahdi", {
        has_nahdi_pharmacy: true,
        provider_name: nahdiForm.provider,
        nahdi_provider_id: nahdiForm.nahdi_provider_id,
        nahdi_provider_token: nahdiForm.nahdi_provider_token,
        show_default_pharmacy: nahdiForm.show_default_pharmacy,
      });
      if (res) {
        toast.success(res?.message || t("Third party pharmacy configured"));
        setPharmacyEnabled(true);
        setShowPharmacyModal(false);
        await refreshSettingsList();
      }
    } finally {
      setPharmacyLoading(false);
    }
  };

  const openPharmacyCredentials = (provider) => {
    setNahdiForm((prev) => ({
      ...prev,
      provider,
    }));
    setShowPharmacyModal(true);
  };

  return (
    <div>
      <div className="space-y-3">
        {showSection(6) && (
          <MedicalReportTemplateSettings
            settings={settings}
            updateSettings={updateSettings}
            updateLoading={updateLoading}
          />
        )}
        {showSection(7) && (
          <SettingsSection
            header={
              <div>
                <h3 className="font-semibold text-gray-800 text-base">
                  {t("Discount Settings")}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("Configure department-wise receptionist discount limits")}
                </p>
              </div>
            }
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <TextInput
                useFormik={false}
                type="number"
                min={0}
                max={100}
                label={t("Default Discount Limit (%)")}
                value={discountConfig.default_limit ?? ""}
                handleChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    discount_settings: {
                      ...discountSetting,
                      discount_settings: {
                        ...discountConfig,
                        default_limit: e.target.value,
                      },
                    },
                  }))
                }
              />
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-2">{t("Department")}</th>
                      <th className="p-2">{t("Discount Limit (%)")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departments.map((department) => {
                      const id = department.id || department._id;
                      const configured = discountConfig.department_limits?.find(
                        (item) => String(item.department_id) === String(id),
                      );
                      return (
                        <tr key={String(id)} className="border-b">
                          <td className="p-2">
                            {department.name_en ||
                              department.name ||
                              department.name_ar ||
                              String(id)}
                          </td>
                          <td className="p-2">
                            <input
                              className="w-32 rounded border border-gray-300 px-2 py-1"
                              type="number"
                              min="0"
                              max="100"
                              value={
                                configured
                                  ? configured.limit
                                  : defaultDiscountLimit
                              }
                              onChange={(e) =>
                                updateDepartmentLimit(id, e.target.value)
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end mt-4">
                <CustomButton
                  label={t("Save")}
                  icon={<FaSave />}
                  onClick={() => updateSettings(discountSetting)}
                  loading={updateLoading}
                  buttonType="primary"
                />
              </div>
            </div>
          </SettingsSection>
        )}
        {showSection(0) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <FaFileInvoice className="text-purple-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("Claims Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Manage claim item submission behavior")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg mb-3"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <label className="text-sm font-medium text-gray-700 block">
                {t("Include zero-price items in claims")}
              </label>
              <div className="flex items-center gap-3 mt-2">
                <InputSwitch
                  checked={
                    settings.claims_settings?.claims_settings
                      ?.include_zero_price_items || false
                  }
                  onChange={(e) =>
                    updateSettings({
                      ...settings.claims_settings,
                      claims_settings: { include_zero_price_items: e.value },
                    })
                  }
                  disabled={loading || updateLoading}
                />
                <span className="text-sm text-gray-600">
                  {settings.claims_settings?.claims_settings
                    ?.include_zero_price_items
                    ? t("Enabled")
                    : t("Disabled")}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t(
                  "Send items with zero price or zero net amount when generating claims",
                )}
              </p>
              <div className="mt-4 space-y-2">
                <label className="text-sm font-medium text-gray-700 block">
                  {t("Enable claim separation above 500")}
                </label>
                <div className="flex items-center gap-3">
                  <InputSwitch
                    checked={
                      settings.claims_settings?.claims_settings
                        ?.max_per_claim_limit || false
                    }
                    onChange={(e) =>
                      updateSettings({
                        ...settings.claims_settings,
                        claims_settings: {
                          ...settings.claims_settings?.claims_settings,
                          max_per_claim_limit: e.value,
                        },
                      })
                    }
                    disabled={loading || updateLoading}
                  />
                  <span className="text-sm text-gray-600">
                    {settings.claims_settings?.claims_settings
                      ?.max_per_claim_limit
                      ? t("Enabled")
                      : t("Disabled")}
                  </span>
                </div>
                <p className="text-xs text-gray-500">
                  {t("Split claims when the amount exceeds 500")}
                </p>
              </div>
            </div>
          </SettingsSection>
        )}

        {showSection(1) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FaFileInvoice className="text-primary text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("Invoice Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Manage invoice size and privacy policy options")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg mb-3"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <DropdownField
                    label={t("Invoice Size")}
                    useFormik={false}
                    options={invoiceSizeOptions}
                    name="invoice_size"
                    onChange={(e) => {
                      onSettingsChange(e);
                      updateSettings({
                        ...settings[e.target.name],
                        value: e.target.value,
                      });
                    }}
                    disabled={loading || updateLoading}
                    value={settings.invoice_size?.value || ""}
                    filter
                    placeholder={t("Select invoice size")}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Choose the default size for printed invoices")}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Privacy Policy")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={settings.invoice_size?.privcy_policy || false}
                      onChange={(e) => {
                        updateSettings({
                          ...settings.invoice_size,
                          privcy_policy: e.value,
                        });
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size?.privcy_policy
                              ? "!bg-primary"
                              : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.privcy_policy
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Display privacy policy on invoices")}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Is Zatca")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={settings.invoice_size?.is_zatca || false}
                      onChange={(e) => {
                        if (e.value === true) {
                          setSettings((prev) => ({
                            ...prev,
                            invoice_size: {
                              ...prev.invoice_size,
                              is_zatca: true,
                            },
                          }));
                        } else {
                          updateSettings({
                            ...settings.invoice_size,
                            is_zatca: false,
                            zatca_settings: {
                              enable_send_cash_invoices: false,
                              enable_send_insurance_invoices: false,
                              enable_send_corporate_invoices: false,
                              enable_send_government_invoices: false,
                              enable_send_deposit_invoices: false,
                              enable_send_withdraw_invoices: false,
                            },
                          });
                        }
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size?.is_zatca ? "!bg-primary" : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.is_zatca
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Enable Zatca on invoices")}
                  </p>
                </div>
                {settings.invoice_size?.is_zatca && (
                  <div className="col-span-full">
                    <div
                      className="border border-gray-200 rounded-lg p-4 bg-white grid grid-cols-1 md:g
rid-cols-2 lg:grid-cols-3 gap-6"
                    >
                      {[
                        {
                          key: "enable_send_cash_invoices",
                          label: t("Send Cash Invoices"),
                        },
                        {
                          key: "enable_send_insurance_invoices",
                          label: t("Send Insurance Invoices"),
                        },
                        {
                          key: "enable_send_corporate_invoices",
                          label: t("Send Corporate Invoices"),
                        },
                        {
                          key: "enable_send_government_invoices",
                          label: t("Send Government Invoices"),
                        },
                        {
                          key: "enable_send_deposit_invoices",
                          label: t("Send Deposit Invoices"),
                        },
                        {
                          key: "enable_send_withdraw_invoices",
                          label: t("Send Withdraw Invoices"),
                        },
                        {
                          key: "enable_edit_zatca_posted_invoice",
                          label: t("Edit Posted Invoice"),
                        },
                        {
                          key: "is_send_patient_amount",
                          label: t("Patient Share Only"),
                        },
                      ].map(({ key, label }) => (
                        <div key={key} className="space-y-2">
                          <label className="text-sm font-medium text-gray-700 block">
                            {label}
                          </label>
                          <div className="flex items-center gap-3">
                            <InputSwitch
                              checked={
                                settings.invoice_size?.zatca_settings?.[key] ||
                                false
                              }
                              onChange={(e) => {
                                const current =
                                  settings.invoice_size?.zatca_settings || {};
                                updateSettings({
                                  ...settings.invoice_size,
                                  zatca_settings: {
                                    enable_send_cash_invoices:
                                      current.enable_send_cash_invoices ||
                                      false,
                                    enable_send_insurance_invoices:
                                      current.enable_send_insurance_invoices ||
                                      false,
                                    enable_send_corporate_invoices:
                                      current.enable_send_corporate_invoices ||
                                      false,
                                    enable_send_government_invoices:
                                      current.enable_send_government_invoices ||
                                      false,
                                    enable_send_deposit_invoices:
                                      current.enable_send_deposit_invoices ||
                                      false,
                                    enable_send_withdraw_invoices:
                                      current.enable_send_withdraw_invoices ||
                                      false,
                                    enable_edit_zatca_posted_invoice:
                                      current.enable_edit_zatca_posted_invoice ||
                                      false,
                                    is_send_patient_amount:
                                      current.is_send_patient_amount || false,
                                    [key]: e.value,
                                  },
                                });
                              }}
                              disabled={loading || updateLoading}
                              pt={{
                                root: { className: "!h-[24px]" },
                                slider: {
                                  className: `rounded-[100px] ${
                                    settings.invoice_size?.zatca_settings?.[key]
                                      ? "!bg-primary"
                                      : ""
                                  }`,
                                },
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Arabic Invoice")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={settings.invoice_size?.is_arabic || false}
                      onChange={(e) => {
                        updateSettings({
                          ...settings.invoice_size,
                          is_arabic: e.value,
                        });
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size?.is_arabic
                              ? "!bg-primary"
                              : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.is_arabic
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Print invoices in Arabic language")}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Allow Update After ERP Posting")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={
                        settings.invoice_size?.allow_update_after_erp_posting ||
                        false
                      }
                      onChange={(e) => {
                        updateSettings({
                          ...settings.invoice_size,
                          allow_update_after_erp_posting: e.value,
                        });
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size
                              ?.allow_update_after_erp_posting
                              ? "!bg-primary"
                              : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.allow_update_after_erp_posting
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      "Allow updating invoices even after they have been posted to ERP",
                    )}
                  </p>
                </div>
              </div>
            </div>
          </SettingsSection>
        )}

        {showSection(2) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FaShieldAlt className="text-primary text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("Insurance Approval Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Choose which approval types are sent automatically")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg mb-3"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  ["auto_send_institutional", "Institutional"],
                  ["auto_send_pharmacy", "Pharmacy"],
                  ["auto_send_oral", "Oral"],
                  ["auto_send_vision", "Vision"],
                  ["auto_send_professional", "Professional"],
                ].map(([key, label]) => {
                  const approvalSettings =
                    settings.invoice_size?.insurance_approval_settings || {};
                  const enabled = approvalSettings[key] !== false;

                  return (
                    <div key={key} className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 block">
                        {t(label)}
                      </label>
                      <div className="flex items-center gap-3">
                        <InputSwitch
                          checked={enabled}
                          onChange={(e) => {
                            updateSettings({
                              ...settings.invoice_size,
                              insurance_approval_settings: {
                                auto_send_institutional:
                                  approvalSettings.auto_send_institutional !==
                                  false,
                                auto_send_pharmacy:
                                  approvalSettings.auto_send_pharmacy !== false,
                                auto_send_oral:
                                  approvalSettings.auto_send_oral !== false,
                                auto_send_vision:
                                  approvalSettings.auto_send_vision !== false,
                                auto_send_professional:
                                  approvalSettings.auto_send_professional !==
                                  false,
                                [key]: e.value,
                              },
                            });
                          }}
                          disabled={loading || updateLoading}
                          pt={{
                            root: { className: "!h-[24px]" },
                            slider: {
                              className: `rounded-[100px] ${enabled ? "!bg-primary" : ""}`,
                            },
                          }}
                        />
                        <span className="text-sm text-gray-600">
                          {enabled ? t("Automatic") : t("Manual")}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {enabled
                          ? t("Send this approval type automatically")
                          : t(
                              "Keep this approval type pending for manual processing",
                            )}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </SettingsSection>
        )}

        {showSection(3) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FaCalendarCheck className="text-blue-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("Visit Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Manage visit and slot menu behavior")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg mb-3"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Slots Detailed Walk-in screen")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={
                        settings.invoice_size?.visit_settings
                          ?.separate_walkin_and_slots || false
                      }
                      onChange={(e) => {
                        updateSettings({
                          ...settings.invoice_size,
                          visit_settings: {
                            separate_walkin_and_slots: e.value,
                          },
                        });
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size?.visit_settings
                              ?.separate_walkin_and_slots
                              ? "!bg-primary"
                              : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.visit_settings
                        ?.separate_walkin_and_slots
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Show Walk-in create menu separately from Slots")}
                  </p>
                </div>
              </div>
            </div>
          </SettingsSection>
        )}

        {showSection(8) && (
          <SettingsSection
            header={
              <div>
                <h3 className="font-semibold text-gray-800 text-base">
                  {t("PayBill Settings")}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {t("Choose how PayBill items are displayed")}
                </p>
              </div>
            }
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("PayBill Display")}
              </label>
              <DropdownField
                useFormik={false}
                options={[
                  { label: t("Category-wise Tabs"), value: "category_tabs" },
                  { label: t("All Items in One Table"), value: "all_items" },
                ]}
                value={
                  settings.invoice_size?.paybill_settings?.display_mode ||
                  "category_tabs"
                }
                handleChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    invoice_size: {
                      ...prev.invoice_size,
                      paybill_settings: { display_mode: e.value },
                    },
                  }))
                }
                disabled={loading || updateLoading}
                placeholder={t("Select PayBill display")}
                className="w-full md:w-96"
              />
              <div className="flex justify-end mt-4">
                <CustomButton
                  label={t("Save")}
                  icon={<FaSave />}
                  onClick={() =>
                    updateSettings({
                      ...settings.invoice_size,
                      paybill_settings: settings.invoice_size
                        ?.paybill_settings || { display_mode: "category_tabs" },
                    })
                  }
                  loading={updateLoading}
                  buttonType="primary"
                />
              </div>
            </div>
          </SettingsSection>
        )}


        {showSection(9) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FaFileInvoice className="text-primary text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("GL Posting Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Choose how revenue is grouped for GL posting")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg mb-3"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("Revenue Grouping")}
              </label>
              <DropdownField
                useFormik={false}
                options={[
                  { label: t("Department"), value: "department" },
                  {
                    label: t("Department + Saudi / Non-Saudi"),
                    value: "department_nationality",
                  },
                ]}
                value={
                  settings.gl_posting_settings?.gl_posting_settings
                    ?.revenue_grouping_mode || "department"
                }
                handleChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    gl_posting_settings: {
                      ...prev.gl_posting_settings,
                      gl_posting_settings: {
                        revenue_grouping_mode: e.value,
                      },
                    },
                  }))
                }
                disabled={loading || updateLoading}
                placeholder={t("Select revenue grouping")}
                className="w-full md:w-96"
              />
              <p className="text-xs text-gray-500 mt-2">
                {t(
                  "Department keeps current posting behavior. Department + Saudi / Non-Saudi splits department revenue by patient nationality.",
                )}
              </p>
              <div className="flex justify-end mt-4">
                <CustomButton
                  label={t("Save")}
                  icon={<FaSave />}
                  onClick={() => updateSettings(settings.gl_posting_settings)}
                  loading={updateLoading}
                  buttonType="primary"
                />
              </div>
            </div>
          </SettingsSection>
        )}

        {showSection(4) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <FaWhatsapp className="text-green-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("WhatsApp Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Configure WhatsApp integration for notifications")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700 block">
                    {t("Enable WhatsApp")}
                  </label>
                  <div className="flex items-center gap-3">
                    <InputSwitch
                      checked={settings.invoice_size?.whatsapp?.status || false}
                      onChange={(e) => {
                        if (e.value === false) {
                          updateSettings({
                            ...settings.invoice_size,
                            whatsapp: {
                              status: e.value,
                              module_name:
                                settings.invoice_size?.whatsapp?.module_name ||
                                null,
                            },
                          });
                        } else {
                          setShowQr(true);
                          setWhatsappValue({
                            target: { name: "invoice_size", value: e.value },
                          });
                        }
                      }}
                      disabled={loading || updateLoading}
                      pt={{
                        root: { className: "!h-[24px]" },
                        slider: {
                          className: `rounded-[100px] ${
                            settings.invoice_size?.whatsapp?.status
                              ? "!bg-green-600"
                              : ""
                          }`,
                        },
                      }}
                    />
                    <span className="text-sm text-gray-600">
                      {settings.invoice_size?.whatsapp?.status
                        ? t("Enabled")
                        : t("Disabled")}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {t(
                      "Enable WhatsApp for sending notifications and invoices",
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <MultiSelectField
                    label={t("WhatsApp Modules")}
                    useFormik={false}
                    options={[
                      { label: t("Visit"), value: "visit" },
                      { label: t("EMR"), value: "emr" },
                      { label: t("Appointment"), value: "appointment" },
                      { label: t("Lab"), value: "lab" },
                    ]}
                    value={settings.invoice_size?.whatsapp?.module_name || []}
                    handleChange={(e) => {
                      updateSettings({
                        ...settings.invoice_size,
                        whatsapp: {
                          status:
                            settings.invoice_size?.whatsapp?.status || false,
                          module_name:
                            e.value && e.value.length > 0 ? e.value : null,
                        },
                      });
                    }}
                    disabled={
                      loading ||
                      updateLoading ||
                      !settings.invoice_size?.whatsapp?.status
                    }
                    placeholder={t("Select modules")}
                    display="chip"
                    filter={false}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("Select which modules should send WhatsApp messages")}
                  </p>
                </div>
              </div>
            </div>
          </SettingsSection>
        )}
        {showSection(5) && (
          <SettingsSection
            header={
              <div className="flex items-center gap-3 py-1">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <FaPrescriptionBottle className="text-indigo-600 text-lg" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 text-base">
                    {t("Third party pharmacy Settings")}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t("Configure pharmacy settings and preferences")}
                  </p>
                </div>
              </div>
            }
            className="shadow-sm border border-gray-200 rounded-lg"
          >
            <div className="bg-gray-50 p-5 rounded-b-lg">
              <div className="space-y-4">
                {pharmacyEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div
                      className="flex items-center justify-between p-3 border border-gray-200 rounded
-lg bg-white"
                    >
                      <div className="space-y-0.5">
                        <label className="text-sm font-semibold text-gray-800 block">
                          {t("Third party pharmacy")}
                        </label>
                        <p className="text-xs text-gray-500">
                          {t("Turn off pharmacy integration")}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">
                          {t("Enabled")}
                        </span>
                        <InputSwitch
                          checked={pharmacyEnabled}
                          onChange={(e) => disablePharmacySettings(e.value)}
                          disabled={pharmacyLoading}
                          pt={{
                            root: { className: "!h-[24px]" },
                            slider: {
                              className: `rounded-[100px] ${
                                pharmacyEnabled ? "!bg-indigo-600" : ""
                              }`,
                            },
                          }}
                        />
                      </div>
                    </div>

                    <div
                      className="flex items-center justify-between p-3 border border-gray-200 rounded
-lg bg-white"
                    >
                      <div className="space-y-0.5">
                        <label className="text-sm font-semibold text-gray-800 block">
                          {t("Show super git Pharmacy")}
                        </label>
                        <p className="text-xs text-gray-500">
                          {t(
                            "Include super git pharmacy in providers list when Nahdi is enabled",
                          )}
                        </p>
                      </div>
                      <InputSwitch
                        checked={nahdiForm.show_default_pharmacy}
                        onChange={async (e) => {
                          const newValue = e.value;
                          setNahdiForm((prev) => ({
                            ...prev,
                            show_default_pharmacy: newValue,
                          }));
                          setPharmacyLoading(true);
                          try {
                            const nahdiSettings =
                              settings?.invoice_size?.nahdi_settings || {};
                            const res = await fetchData(
                              "POST",
                              "/v3/settings/nahdi",
                              {
                                has_nahdi_pharmacy: true,
                                provider_name:
                                  nahdiSettings?.provider_name ||
                                  nahdiForm.provider,
                                nahdi_provider_id:
                                  nahdiSettings?.nahdi_provider_id ||
                                  nahdiForm.nahdi_provider_id,
                                nahdi_provider_token:
                                  nahdiSettings?.nahdi_provider_token ||
                                  nahdiForm.nahdi_provider_token,
                                show_default_pharmacy: newValue,
                              },
                            );
                            if (res) {
                              toast.success(
                                res?.message || t("Setting updated"),
                              );
                              await refreshSettingsList();
                            }
                          } finally {
                            setPharmacyLoading(false);
                          }
                        }}
                        disabled={pharmacyLoading}
                        pt={{
                          root: { className: "!h-[24px]" },
                          slider: {
                            className: `rounded-[100px] ${
                              nahdiForm.show_default_pharmacy
                                ? "!bg-indigo-600"
                                : ""
                            }`,
                          },
                        }}
                      />
                    </div>
                  </div>
                )}
                {!pharmacyEnabled && (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 block">
                        {t("Provider")}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        {t(
                          "Select provider to enter credentials and enable integration",
                        )}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      {providers.map((provider) => (
                        <button
                          key={provider.value}
                          type="button"
                          onClick={() =>
                            openPharmacyCredentials(provider.value)
                          }
                          disabled={pharmacyLoading}
                          className="border rounded-lg p-3 text-left transition bg-white border-gray-200
 hover:border-indigo-300"
                        >
                          <p className="text-sm font-semibold text-gray-800 mb-2">
                            {provider.label}
                          </p>
                          <div
                            className="w-full h-28 bg-white rounded-md overflow-hidden flex items-cen
ter justify-center border border-gray-100"
                          >
                            {provider.img ? (
                              <img
                                src={provider.img}
                                alt={provider.label}
                                className="w-full h-full object-contain p-1"
                              />
                            ) : (
                              <span className="text-xl font-semibold text-gray-500">
                                {provider.label?.charAt(0)}
                              </span>
                            )}
                          </div>
                          <div className="mt-3 flex justify-end">
                            <span
                              className="inline-flex items-center gap-1 text-xs font-medium text-ind
igo-600"
                            >
                              <FaEdit />
                              {t("Enter credentials")}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </SettingsSection>
        )}
      </div>

      <ReusableModal
        position="top"
        title={t("WhatsApp Integration")}
        isVisible={showQr}
        HeaderIcon={FaQrcode}
        onClose={() => {
          setShowQr(false);
        }}
      >
        <WhatsappQr updateWhatsapp={updateWhatsapp} />
      </ReusableModal>

      <ReusableModal
        position="top"
        title={t("Provider Credentials")}
        isVisible={showPharmacyModal}
        HeaderIcon={FaPrescriptionBottle}
        onClose={() => setShowPharmacyModal(false)}
      >
        <div className="space-y-4 p-2">
          <div className="grid grid-cols-1 gap-4">
            {/* <div className='flex items-center justify-between gap-3 rounded-lg border border-indigo-10
0 bg-indigo-50 p-3'>
              <div>
                <p className='text-xs text-gray-500'>
                  {t('Selected Provider')}
                </p>
                <p className='text-sm font-semibold text-gray-800'>
                  {providers.find(
                    provider => provider.value === nahdiForm.provider
                  )?.label || nahdiForm.provider}
                </p>
              </div>
              <CustomButton
                label={t('Change Provider')}
                icon={<FaEdit />}
                onClick={() => setShowPharmacyModal(false)}
                buttonType='reset'
              />
            </div> */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput
                useFormik={false}
                label={t("Provider ID")}
                name="nahdi_provider_id"
                placeholder={t("Enter provider ID")}
                value={nahdiForm.nahdi_provider_id}
                handleChange={(e) =>
                  setNahdiForm((prev) => ({
                    ...prev,
                    nahdi_provider_id: e.target.value,
                  }))
                }
              />
              <TextInput
                useFormik={false}
                label={t("Provider Token")}
                name="nahdi_provider_token"
                placeholder={t("Enter provider token")}
                value={nahdiForm.nahdi_provider_token}
                handleChange={(e) =>
                  setNahdiForm((prev) => ({
                    ...prev,
                    nahdi_provider_token: e.target.value,
                  }))
                }
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FaLink className="text-blue-600" />
              {t("Setup Instructions")}
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <p className="flex items-start gap-2">
                <FaCog className="text-blue-600 mt-1" />
                {t("Use Provider ID and Provider Token shared by Provider")}
              </p>
              {/* <p className='flex items-start gap-2'>
                <FaCog className='text-blue-600 mt-1' />
                {t('Do not send provider credentials from frontend stock APIs')}
              </p> */}
              <p className="flex items-start gap-2">
                <FaCog className="text-blue-600 mt-1" />
                {t(
                  "System will use saved subscription credentials automatically",
                )}
              </p>
            </div>
          </div>

          <div className="flex justify-end">
            <CustomButton
              label={t("Save Pharmacy Settings")}
              icon={<FaSave />}
              onClick={saveNahdiSettings}
              loading={pharmacyLoading}
              buttonType="primary"
            />
          </div>
        </div>
      </ReusableModal>
    </div>
  );
}

export default GeneralSettings;
